import React, { useEffect, useState, useRef } from "react";
import { getBMPBuffer } from "../../../api/getBMPBuffer";
import { getBMPInvestments } from "../../../api/getBMPInvestments";
import { BMP_Buffer, BMP_Investment } from "../../../models/bmp.model";
import Decimal from "decimal.js";

interface MonthlyInvestmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
}

export const AddMonthlyInvestmentModal: React.FC<MonthlyInvestmentModalProps> = ({ isOpen, onClose, onSubmit }) => {
    const [buffers, setBuffers] = useState<BMP_Buffer[]>([]);
    const [filteredBuffers, setFilteredBuffers] = useState<BMP_Buffer[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    
    const [investments, setInvestments] = useState<BMP_Investment[]>([]);
    const [selectedInvestmentId, setSelectedInvestmentId] = useState<number | "">("");
    
    const [selectedBuffer, setSelectedBuffer] = useState<BMP_Buffer | null>(null);
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [investedUsd, setInvestedUsd] = useState<string>("");

    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            const loadBuffers = async () => {
                try {
                    const data = await getBMPBuffer();
                    setBuffers(data.filter((e) => e.pufferedUsd && Number(e.pufferedUsd) > 0));
                    setFilteredBuffers(data.filter((e) => e.pufferedUsd && Number(e.pufferedUsd) > 0));
                } catch (error) {
                    console.error("Failed to load buffers", error);
                }
            };
            loadBuffers();
        }
    }, [isOpen]);

    useEffect(() => {
        const loadUserInvestments = async () => {
            if (selectedBuffer?.user_id) {
                try {
                    const data = await getBMPInvestments(selectedBuffer.user_id);
                    setInvestments(data);
                    if (data.length > 0) setSelectedInvestmentId(data[0].id);
                } catch (error) {
                    console.error("Failed to load user investments", error);
                }
            } else {
                setInvestments([]);
                setSelectedInvestmentId("");
            }
        };
        loadUserInvestments();
    }, [selectedBuffer]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = buffers.filter(item => 
            item.user?.name?.toLowerCase().includes(lowerSearch) || 
            item.user?.email?.toLowerCase().includes(lowerSearch)
        );
        setFilteredBuffers(filtered);
    }, [searchTerm, buffers]);

    const handleSelectBuffer = (buffer: BMP_Buffer) => {
        setSelectedBuffer(buffer);
        setSearchTerm(buffer.user?.name || "");
        setIsDropdownOpen(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!selectedBuffer) return alert("Kérlek válassz ügyfelet!");
        
        // Always parse to Decimal for safe comparison
        const amount = new Decimal(investedUsd || 0);
        const max = new Decimal(selectedBuffer.pufferedUsd || 0);
    
        if (amount.lte(0)) return alert("Az összegnek nagyobbnak kell lennie nullánál!");
    
        if (amount.gt(max)) {
            return alert(`Hiba: Nincs elég fedezet a pufferben! (Elérhető: ${max.toNumber()} USD)`);
        }
    
        try {
            // Wait for the submission to complete
            await onSubmit({
                user_id: selectedBuffer.user_id,
                bmp_investment_id: Number(selectedInvestmentId),
                date,
                investedUsd: amount.toNumber(),
            });
            
            // IMPORTANT: Clear the selected buffer after success 
            // so the next time it's opened, it fetches fresh data
            setSelectedBuffer(null);
            setSearchTerm("");
            setInvestedUsd("");
        } catch (error) {
            console.error("Submission failed", error);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-blue/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-cream">
                <div className="flex items-center justify-between p-6 bg-cream/30 border-b border-cream">
                    <h2 className="text-xl font-bold text-dark-brown">Havi befektetés rögzítése</h2>
                    <button onClick={onClose} className="text-light-blue hover:text-error">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    <div className="relative" ref={dropdownRef}>
                        <label className="block text-sm font-semibold text-dark-blue mb-1">Ügyfél (Puffer alapján)</label>
                        <input
                            type="text"
                            placeholder="Keresés..."
                            className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setIsDropdownOpen(true);
                                setSelectedBuffer(null);
                            }}
                            onFocus={() => setIsDropdownOpen(true)}
                        />
                        {isDropdownOpen && filteredBuffers.length > 0 && (
                            <div className="absolute z-20 w-full mt-1 bg-white border border-cream rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                {filteredBuffers.map((item) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        className="w-full text-left px-4 py-3 hover:bg-ultra-light/50 border-b last:border-0 flex justify-between items-center"
                                        onClick={() => handleSelectBuffer(item)}
                                    >
                                        <div className="flex flex-col">
                                            <span className="font-semibold">{item.user?.name}</span>
                                            <span className="text-xs text-light-blue">{item.user?.email}</span>
                                        </div>

                                        <span className="text-sm font-bold text-success bg-success/10 px-2 py-1 rounded">
                                            {Number(item.pufferedUsd).toLocaleString('hu-HU')} USD
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {selectedBuffer && (
                        <div>
                            <label className="block text-sm font-semibold text-dark-blue mb-1">Cél befektetés</label>
                            <select
                                required
                                value={selectedInvestmentId}
                                onChange={(e) => setSelectedInvestmentId(Number(e.target.value))}
                                className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg text-dark-blue"
                            >
                                <option value="" disabled>Válassz befektetést...</option>
                                {investments.map((inv) => (
                                    <option key={inv.id} value={inv.id}>
                                        {new Date(inv.date).toLocaleDateString()} - Net: {inv.netInvestedUsd?.toString()} USD
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-semibold text-dark-blue mb-1">Dátum</label>
                        <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg" />
                    </div>

                    <div>
                        <div className="flex justify-between mb-1">
                            <label className="block text-sm font-semibold text-dark-blue">Befektetett összeg (USD)</label>
                            {selectedBuffer && (
                                <span 
                                    className="text-xs font-bold text-light-blue cursor-pointer hover:text-dark-blue transition-colors"
                                    onClick={() => setInvestedUsd(selectedBuffer.pufferedUsd?.toString() || "")}
                                    title="Kattints a teljes összeg kitöltéséhez"
                                >
                                    Max: {selectedBuffer.pufferedUsd?.toString()} USD
                                </span>
                            )}
                        </div>
                        <input
                            type="number"
                            required
                            step="any"
                            value={investedUsd}
                            onChange={(e) => setInvestedUsd(e.target.value)}
                            className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg font-mono"
                            placeholder="0.00"
                        />
                    </div>

                    <div className="flex gap-3 pt-4 border-t border-cream">
                        <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg border border-light-blue/30 text-light-blue">Mégse</button>
                        <button type="submit" disabled={!selectedInvestmentId} className="flex-1 px-4 py-2.5 rounded-lg bg-brown text-white font-semibold disabled:opacity-50">Mentés</button>
                    </div>
                </form>
            </div>
        </div>
    );
};